import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { PageBodyContainer } from "app/components/Common/PageBodyContainer";
import { useSelector, useDispatch } from "react-redux";
import { CustomDimensions } from "styles/theme/themes";
import { getSubjectIcon, getSubjectColor, getPaperColor } from "app/utils";
import { UploadCard } from "app/components/Assignment/UploadCard";
import { useS3Upload } from "app/hooks/S3Hooks";
import { PutCommand } from "@aws-sdk/lib-dynamodb";
import { ASSIGNMENT_SUBMISSION_STATES } from "app/constants";
import { useAuth } from "aws/AuthContext";
import { submitExam, getSubmission } from "aws/lambda";
import { selectExamStore, selectIsExamEnrolled } from "store/ExamStore/slice/selector";
import { mapExamStatus } from "app/components/ExamStatus/utils/mapExamStatus";
import { fetchEnrolledExams } from "store/ExamStore/slice";
import { getExamById } from "aws/dynamodb";
import { TypeExam } from "app/constants/types/exam.types";
import { refactorExamList } from "store/ExamStore/slice";
import useExamExplanation from "./useExamExplanation";
import { TypeSubmission } from "./types";
import { SUBMISSION_LIMIT } from "app/constants/content";

const pulseKeyframes = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.01); }
    100% { transform: scale(1); }
  }
`;

const initialState = {
  submissions: {
    examId: undefined,
    submissionArtifact: undefined,
    updatedAt: undefined,
    status: ASSIGNMENT_SUBMISSION_STATES.NOT_SUBMITTED,
    score: undefined,
    rawData: undefined,
  }
};

const useSubmission = () => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const dispatch = useDispatch();
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user, signInAnonymously } = useAuth();
  const { uploadToS3 } = useS3Upload();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [displayName, setDisplayName] = useState(() => {
    const saved = localStorage.getItem("displayName");
    return saved || "Student";
  });

  const {
    returnedPapers,
    setReturnedPapers,
    explanationVideos,
    setExplanationVideos,
  } = useExamExplanation({})

  const [exam, setExam] = React.useState<TypeExam | undefined>();
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionFile, setSubmissionFile] = useState<
    File | FileList | undefined
  >(undefined);
  // Verification checkbox states
  const [verificationChecks, setVerificationChecks] = useState({
    selfDone: false,
    timeLimit: false,
    confirmTruth: false,
  });

  // Grace period states
  const [isWithinGracePeriod, setIsWithinGracePeriod] = useState(false);
  const [remainingGraceTime, setRemainingGraceTime] = useState('');

  // Submission status polling states
  const [submissionStatus, setSubmissionStatus] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Exam status polling states
  const [examPolling, setExamPolling] = useState(false);
  const examPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionJustCompleted, setSubmissionJustCompleted] = useState(false);
  const [submissions, setSubmissions] = useState<TypeSubmission>(initialState.submissions);

  // Get assignment data directly from DynamoDB
  const [examLoading, setExamLoading] = useState(true);

  useEffect(() => {
    // Reset submission state when component mounts
    setSubmissions(initialState.submissions)

    // Add keyframes to document
    const style = document.createElement("style");
    style.innerHTML = pulseKeyframes;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [dispatch]);

  // Handle submission error
  useEffect(() => {
    if (submissionError) {
      setIsSubmitting(false);
      toast.error(submissionError);
    }
  }, [submissionError]);

  // Track current user ID for submission reloading
  const currentUserId = useMemo(() => {
    return user?.userId || "";
  }, [user]);

  React.useEffect(() => {
    const checkAuth = async () => {
      if (!user || !user.userId) {
        // If not logged in, show welcome modal
        setShowWelcomeModal(true);
      }
    };

    checkAuth();
  }, [user]);

  // Function to fetch submission status using Lambda
  const fetchSubmissionStatus = async () => {
    if (!examId || !user?.userId) return;

    setIsPolling(true);
    try {
      console.log(`Polling submission status for exam: ${examId}`);

      const result = await getSubmission(examId);

      setSubmissionStatus(result);

      // If submission is confirmed, update store and stop polling
      if (result && result.submitted) {
        // Update submission in store if needed
        if (result.presignedUrl) {
          setSubmissions({
            examId: examId,
            submissionArtifact: result?.presignedUrl,
            resultUris: result?.resultUris,
            updatedAt: result?.submissionAt && dayjs(result.submissionAt * 1000).format("DD MMM YYYY, HH:mm"),
            status: result?.submitted ? ASSIGNMENT_SUBMISSION_STATES.SUBMITTED : ASSIGNMENT_SUBMISSION_STATES.NOT_SUBMITTED,
            score: result?.resultData?.score,
            rawData: result,
          })

          if (result?.resultUris) {
            const arrPapers = Array.from(result?.resultUris)

            if (arrPapers?.length > 0) {
              const papers = arrPapers?.map((item) => ({ url: item as string }))
              const refactorPapers = [...(returnedPapers || []), ...papers]
              setReturnedPapers(refactorPapers)
            }
          }
        }

        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error("Error fetching submission status:", error);
      // Don't show error toast on every poll failure to avoid spamming the user
    } finally {
      setIsPolling(false);
    }
  };

  // Effect to immediately check submission status after new submission
  useEffect(() => {
    if (submissionJustCompleted && examId && user?.userId) {
      console.log("New submission detected, fetching submission status 3s later...");
      
      setTimeout(() =>{
      // Reset any existing polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Immediately fetch the status
      fetchSubmissionStatus();
      
      // Set up a new polling interval
      pollingIntervalRef.current = setInterval(fetchSubmissionStatus, 10000);
      
      // Reset the flag
      setSubmissionJustCompleted(false);
      }, 3000)

    }
  }, [submissionJustCompleted, examId, user?.userId]);

  // Set up polling for submission status
  useEffect(() => {
    // Only start polling if exam exists and status is appropriate for polling
    if (!examId || !user?.userId || !exam || exam.status === "standby-locked" || exam.status === "standby-open" || exam.status === "pending") {
      return;
    }

    // Initial fetch
    fetchSubmissionStatus();

    // Set up polling interval (every 10 seconds)
    pollingIntervalRef.current = setInterval(fetchSubmissionStatus, 10000);

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [examId, user?.userId, exam?.status]);

  // Function to fetch exam status
  const fetchExamStatus = async () => {
    if (!examId) return;

    setExamPolling(true);

    try {
      const response = await getExamById(examId);

      if (response) {
        const refactoredExams = await refactorExamList([response]);

        // Get the basic exam
        const basicExam = refactoredExams[0];

        // Safely handle the exam mapping
        if (basicExam) {
          try {
            // Get submission status
            const hasSubmission = basicExam.status === 'submitted';
            
            // Map the status based on enrollment status - handle possible missing fields
            const mappedStatus = mapExamStatus(
              basicExam.rawData?.status || basicExam.status, // Fallback to status if rawData.status is undefined
              isExamEnrolled,
              basicExam.examTime,
              basicExam.examDuration,
              hasSubmission
            );
            
            // Create a new exam object with the mapped status
            const mappedExam = {
              ...basicExam,
              status: mappedStatus,
              originalStatus: basicExam.status // Keep original status for reference
            };

            // Update the exam state
            setExam(mappedExam);
          } catch (error) {
            console.error('Error mapping exam status:', error);
            // Fall back to setting the basic exam without mapping
            setExam(basicExam);
          }
        } else {
          setExam(basicExam);
        }

        if (response?.explanationArtifactUris) {
          const arrVideos = Array.from(response?.explanationArtifactUris)

          if (arrVideos?.length > 0) {
            const videos = arrVideos?.map(item => ({ url: item }))
            setExplanationVideos(videos)
          }
        }
      } else {
        console.error('Exam not found');
        setExam(undefined);
      }
    } catch (error) {
      console.error('Error refreshing exam:', error);
      setExam(undefined);
    } finally {
      setExamPolling(false);
    }
  };

  // Fetch enrolled exams
  React.useEffect(() => {
    console.log("Fetching enrolled exams...", user);
    
    if (user?.userId) {
      dispatch(fetchEnrolledExams());
    }
  }, [dispatch, user?.userId]);

  // Check if the current exam is enrolled (default to true on submission page)
  // Since the user is already on the submission page, they must be enrolled
  const isExamEnrolled = true;

  // Initial exam load and polling setup
  React.useEffect(() => {
    if (!examId) {
      setExamLoading(false);
      return;
    }

    // Initial load
    setExamLoading(true);
    fetchExamStatus().finally(() => {
      setExamLoading(false);
    });

    // Set up polling interval (15 seconds)
    examPollingIntervalRef.current = setInterval(() => {
      // Don't set examLoading during polling updates
      fetchExamStatus();
    }, 15000);

    // Cleanup
    return () => {
      if (examPollingIntervalRef.current) {
        clearInterval(examPollingIntervalRef.current);
        examPollingIntervalRef.current = null;
      }
    };
  }, [examId]); // Only depend on examId

  // Save displayName to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem("displayName", displayName);
  }, [displayName]);

  const handleSaveDisplayName = async () => {
    if (!displayName.trim()) return;

    setIsSigningUp(true);
    try {
      // First sign in anonymously using AWS
      const authResult = await signInAnonymously();
      if (!authResult || !authResult.userId)
        throw new Error("Failed to sign in anonymously");

      const userId = authResult.userId;

      // Save to DynamoDB Student table first
      const command = new PutCommand({
        TableName: "Student",
        Item: {
          StudentID: userId,
          GoogleID: userId,
          Name: displayName,
          CreatedAt: new Date().toISOString(),
        },
      });

      // No need for Firebase operations since we're fully on AWS now
      setShowWelcomeModal(false);
      toast.success(`Welcome, ${displayName}!`);

      // Redirect to user-specific path
      // navigate(`/student/${examId}/${userId}`, { replace: true });
    } catch (error) {
      console.error("Error saving user data:", error);
      toast.error("Failed to sign up. Please try again.");
    } finally {
      setIsSigningUp(false);
    }
  };

  const executeSubmission = async () => {
    console.log("Submitting exam...", user);
    if (!user || !user.userId) {
      toast.error("You must be logged in to submit");
      return;
    }

    if (!examId || !submissionFile) return;

    // Verify all checkboxes are checked
    if (
      !verificationChecks.selfDone ||
      !verificationChecks.timeLimit ||
      !verificationChecks.confirmTruth
    ) {
      toast.error(
        "Please confirm all verification statements before submitting"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const file =
        submissionFile instanceof FileList ? submissionFile[0] : submissionFile;

      console.log(`Submitting exam: ${examId}`);

      // Step 1: Get presigned URL from Lambda function
      const submission = await submitExam(examId);

      if (!submission.presignedUrl) {
        throw new Error("Failed to get presigned URL for submission");
      }

      console.log("Received presigned URL for upload:", submission);

      // Step 2: Upload file using the presigned URL
      console.log("Uploading file using presigned URL...");
      const uploadResponse = await fetch(submission.presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      console.log("File uploaded successfully");

      // Reset form state
      setSubmissionFile(undefined);
      setVerificationChecks({
        selfDone: false,
        timeLimit: false,
        confirmTruth: false
      });

      // Set flag to trigger immediate status check
      setSubmissionJustCompleted(true);

      // Step 3: Success notification
      toast.success("Submission successful!");

      setIsSubmitting(false);
    } catch (error) {
      console.error("Error in submission process:", error);
      toast.error(
        `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setIsSubmitting(false);
    }
  }

  const handleSubmit = async () => {
    if (SUBMISSION_LIMIT > 0) {
      handleOpenModal()
    }
    else {
      await executeSubmission()
    }
  };
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  const handleConfirmSubmit = async () => {
    setModalOpen(false);
    await executeSubmission()
  };
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // max 100MB

  const handleSelectFile = (file: File | FileList | undefined) => {
    setSubmissionFile(file);
  };

  const handleRemoveFile = () => {
    setSubmissionFile(undefined);
  };

  // Function to test getSubmission endpoint
  const handleTestGetSubmission = async () => {
    if (!examId) {
      toast.error("No assignment ID found");
      return;
    }

    try {
      console.log(`Testing getSubmission for exam: ${examId}`);
      const result = await getSubmission(examId);
      console.log("GET SUBMISSION TEST RESULT:", result);
      toast.success("Check console for submission details");
    } catch (error) {
      console.error("Error testing getSubmission:", error);
      toast.error(
        `Failed to get submission: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // Calculate grace period status
  const calculateGracePeriod = useCallback(() => {
    if (!exam?.examTime || !exam?.examDuration) return;

    // Parse the formatted date string using dayjs
    const examStartTime = dayjs(exam.examTime, "dddd DD MMM YYYY, HH:mm").valueOf();

    // Parse duration from "HH:mm:ss" format to minutes
    const durationParts = exam.examDuration.split(':');

    // Convert duration to minutes, handling various formats
    let durationInMinutes = 0;
    if (exam.examDuration.includes('hours')) {
      // Handle "X hours" format
      const hours = Number(exam.examDuration.split(' ')[0]);
      durationInMinutes = hours * 60;
    } else if (exam.examDuration.includes('h')) {
      // Handle "Xh Ym" format
      const hours = Number(exam.examDuration.split('h')[0]);
      const minutes = exam.examDuration.includes('m') ?
        Number(exam.examDuration.split('h')[1].split('m')[0]) : 0;
      durationInMinutes = (hours * 60) + minutes;
    } else if (durationParts.length >= 2) {
      // Handle "HH:mm:ss" format
      durationInMinutes = (Number(durationParts[0]) * 60) + Number(durationParts[1]);
    }

    const endTime = examStartTime + (durationInMinutes * 60 * 1000);
    const graceEndTime = endTime + (60 * 60 * 1000); // 1 hour after exam end
    const now = Date.now();

    const withinGrace = now > endTime && now < graceEndTime;

    setIsWithinGracePeriod(withinGrace);

    if (withinGrace) {
      const remaining = Math.floor((graceEndTime - now) / 1000);
      setRemainingGraceTime(
        `${Math.floor(remaining / 60)}m ${remaining % 60}s`
      );
    }
  }, [exam]);

  // Update grace period status every second when within grace period
  useEffect(() => {
    calculateGracePeriod();

    if (isWithinGracePeriod) {
      const interval = setInterval(calculateGracePeriod, 1000);
      return () => clearInterval(interval);
    }
  }, [isWithinGracePeriod, calculateGracePeriod]);

  // Get current user's submission from store
  const currentUserSubmission = useMemo(() => {
    if (examId == submissions.examId) {
      return submissions
    }
    else {
      setSubmissions(initialState.submissions)
    }
  }, [submissions, examId, currentUserId]);

  const hasSubmission = currentUserSubmission;
  const isDueDatePassed = false

  const handleVerificationChange = (field: string, value: boolean) => {
    setVerificationChecks((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return {
    handleOpenModal,
    handleCloseModal,
    handleConfirmSubmit,
    isModalOpen,
    setModalOpen,
    MAX_FILE_SIZE,
    navigate,
    setDisplayName,
    examId,
    showWelcomeModal,
    displayName,
    isSigningUp,
    setIsSigningUp,
    isSubmitting,
    setIsSubmitting,
    submissionFile,
    setSubmissionFile,
    submissionSuccess,
    submissionError,
    submissions,
    exam,
    loading: examLoading,
    error: false,
    currentUserId,
    currentUserSubmission,
    handleSaveDisplayName,
    handleSubmit,
    handleSelectFile,
    handleRemoveFile,
    handleTestGetSubmission,
    hasSubmission,
    isDueDatePassed,
    verificationChecks,
    handleVerificationChange,
    // Grace period states
    isWithinGracePeriod,
    remainingGraceTime,
    // Submission status polling states and functions
    submissionStatus,
    isPolling,
    fetchSubmissionStatus,
    // Exam status polling states and functions
    examPolling,
    fetchExamStatus,
    returnedPapers,
    explanationVideos,
  };
};

export default useSubmission;
